import { Component } from '@angular/core';
import { PagetoolbarComponent } from '../pagetoolbar/pagetoolbar.component';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { PairService } from '../../services/pair.service';
import { MatDialog } from '@angular/material/dialog';
import { MatchingPeriodService } from '../../services/matchingPeriod.service';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { FormControl } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatInputModule } from '@angular/material/input';
import { distinctUntilChanged } from 'rxjs/operators';
import { debounceTime } from 'rxjs/operators';
import { HighlightPipe } from '../../pipes/highlight.pipe';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { Recipient } from '../../interfaces/recipient';
import { Approval, ApprovalStatus } from '../../interfaces/approval';
import { ApprovalBoxComponent } from './approval-box/approval-box.component';
import { ParticipantService } from '../../services/participant.service';
import { closeDialogComponent as CloseDialogComponent } from './close-dialog/close-dialog.component';
import { ApproveRequestDialogComponent } from './approve-request-dialog/approve-request-dialog.component';
import { AuthService } from '../../auth/auth.service';
import { Permission, User } from '../../interfaces/permission';

@Component({
  selector: 'app-approvals',
  imports: [
    PagetoolbarComponent,
    MatIconModule,
    CommonModule,
    MatExpansionModule,
    MatNativeDateModule,
    MatDatepickerModule,
    ApprovalBoxComponent,
    MatButtonToggleModule,
    ReactiveFormsModule,
    MatInputModule,
    HighlightPipe,
    MatButtonModule,
    MatMenuModule
  ],
  templateUrl: './approvals.component.html',
  styleUrl: './approvals.component.scss'
})
export class ApprovalsComponent {
  approvals: Approval[] = [];
  filteredApprovals: Approval[] = [];
  _filterButtons = new FormControl('pending');
  searchControl = new FormControl('');
  ApprovalStatus = ApprovalStatus; // Expose the enum to the template

  updateApprovals: boolean = false;
  canDeleteApproval: boolean = false;
  isCenter: boolean = false;
  userCenterName: string | null = null;

  constructor(
    private snackBar: MatSnackBar,
    private participantService: ParticipantService,
    private readonly dialog: MatDialog,
    private readonly authService: AuthService
  ) {}

  ngOnInit(): void {
    this.authService.getAllPermissions$().subscribe((permissions) => {
      this.updateApprovals = permissions.includes(Permission.PRIORITY_DENY) || permissions.includes(Permission.PRIORITY_APPROVAL);
      this.canDeleteApproval =
        permissions.includes(Permission.PRIORITY_REQUEST) ||
        permissions.includes(Permission.PRIORITY_DENY) ||
        permissions.includes(Permission.PRIORITY_APPROVAL);
      this.isCenter = permissions.includes(Permission.PRIORITY_REQUEST) &&
        !permissions.includes(Permission.PRIORITY_APPROVAL) &&
        !permissions.includes(Permission.PRIORITY_DENY);
    });

    this.authService.getUser().subscribe((user: User) => {
      this.userCenterName = user?.centerName || null;
    });

    this.loadApprovals();

    this._filterButtons.valueChanges.subscribe((filter) => {
      if (filter == null) {
        filter = 'pending';
      }
      this.applyFilter(filter);
    });

    this.searchControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe((value) => {
      this.applySearch(value);
    });
  }

  private loadApprovals(): void {
    this.participantService.getAllApprovals().subscribe({
      next: (data) => {
        this.approvals = data;
        this.filteredApprovals = data;
        this.applyFilter('pending');
        console.log(data);
      },
      error: (error) => {
        console.log(error);
      }
    });
  }

  private applyFilter(filter: string) {
    if (filter === 'all') {
      this.filteredApprovals = this.approvals;
    } else if (filter === 'pending') {
      this.filteredApprovals = this.approvals
        .filter((approval) => approval.status === ApprovalStatus.PENDING)
        .sort((a, b) => {
          const dateA = a?.dateCreated ? new Date(a.dateCreated).getTime() : 0;
          const dateB = b?.dateCreated ? new Date(b.dateCreated).getTime() : 0;
          return dateA - dateB;
        });
    } else if (filter === 'approved') {
      this.filteredApprovals = this.approvals
        .filter((approval) => approval.status === ApprovalStatus.APPROVED)
        .sort((a, b) => {
          const dateA = a?.dateCreated ? new Date(a.dateCreated).getTime() : 0;
          const dateB = b?.dateCreated ? new Date(b.dateCreated).getTime() : 0;
          return dateB - dateA;
        });
    } else if (filter === 'denied') {
      this.filteredApprovals = this.approvals
        .filter((approval) => approval.status === ApprovalStatus.DENIED)
        .sort((a, b) => {
          const dateA = a?.dateCreated ? new Date(a.dateCreated).getTime() : 0;
          const dateB = b?.dateCreated ? new Date(b.dateCreated).getTime() : 0;
          return dateB - dateA;
        });
    }
  }

  private applySearch(searchTerm: string | null): void {
    if (searchTerm == null) {
      this.filteredApprovals = this.approvals;
      return;
    }

    searchTerm = searchTerm.toLowerCase();

    // Filter pairs based on names or enis number
    this.filteredApprovals = this.approvals.filter(
      (approval) =>
        approval.recipient.firstName?.toLowerCase().includes(searchTerm!) ||
        approval.recipient.lastName?.toLowerCase().includes(searchTerm!) ||
        (approval.recipient.enisNumber && approval.recipient.enisNumber.toString().includes(searchTerm!))
    );
  }

  denyApproval(approval: Approval) {
    const dialogRef = this.dialog.open(CloseDialogComponent, {
      data: approval
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.approvals = this.approvals.filter((item) => (item = approval));
        this.applyFilter(this._filterButtons.value ?? 'pending');
      }
    });
  }

  approve(approval: Approval) {
    const dialogRef = this.dialog.open(ApproveRequestDialogComponent, {
      data: approval
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.approvals = this.approvals.filter((item) => (item = approval));
        this.applyFilter(this._filterButtons.value ?? 'pending');
      }
    });
  }

  canDeleteSpecificApproval(approval: Approval): boolean {
    if (!this.canDeleteApproval) {
      return false;
    }

    // Only pending approvals can be deleted
    if (approval.status !== ApprovalStatus.PENDING) {
      return false;
    }

    // Centers can only delete their own approvals
    if (this.isCenter) {
      return approval.recipient?.center?.name === this.userCenterName;
    }

    // Operators can delete any pending approval
    return true;
  }

  deleteApproval(approval: Approval) {
    if (!approval.id) {
      this.snackBar.open('Cannot delete approval: No approval ID', 'Close', { duration: 3000 });
      return;
    }

    const confirmDelete = confirm(
      `Are you sure you want to delete this pending ${approval.approvalColumn} request for ${approval.recipient.firstName} ${approval.recipient.lastName}?`
    );

    if (!confirmDelete) {
      return;
    }

    this.participantService.deleteApproval(approval.id).subscribe({
      next: () => {
        this.snackBar.open('Approval request deleted successfully', 'Close', { duration: 3000 });
        // Remove from local array
        this.approvals = this.approvals.filter((item) => item.id !== approval.id);
        this.applyFilter(this._filterButtons.value ?? 'pending');
      },
      error: (error) => {
        const errorMessage = error.message || 'Failed to delete approval request';
        this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
      }
    });
  }
}
